import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import {
  ORDERS_DECOPROF_EXCLUDED_SUPPLIERS,
  mapSupplierToType,
  type OrderDecoprofType,
  type OrderDecoprofStatus,
} from "@/lib/ordersDecoprof";

export interface OrdersDecoprofRow {
  ordernumber: string;
  /** Interne Lightspeed orderid — nodig om de pakbon op te halen via de API */
  orderid: number | null;
  /** Eerste niet-uitgesloten leverancier op de order, gebruikt voor type-mapping */
  primarySupplier: string | null;
  /** Alle leveranciers die op deze order voorkomen (na excludes) */
  suppliers: string[];
  type: OrderDecoprofType;
  status: OrderDecoprofStatus;
}

interface RawRow {
  orderid: number | null;
  ordernumber: string | null;
  suppliertitle: string | null;
  orders_decoprof_status: string | null;
  createdat: string | null;
}

/**
 * Haalt orderregels op en groepeert deze tot één order per ordernummer.
 * Een order is relevant zodra minimaal 1 regel een leverancier heeft die
 * NIET in de exclude-lijst staat.
 */
export const useOrdersDecoprof = () => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();

  return useQuery({
    queryKey: ["orders-decoprof"],
    staleTime: 30_000,
    enabled: isAuthenticated && isSessionValid && !isValidating,
    queryFn: async (): Promise<OrdersDecoprofRow[]> => {
      // Beperk tot recente orders + filter uitgesloten leveranciers server-side
      // om timeouts op de 50k+ tabel te voorkomen.
      const sinceIso = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
      const excludedList = Array.from(ORDERS_DECOPROF_EXCLUDED_SUPPLIERS);
      const excludedCsv = `(${excludedList.map((s) => `"${s}"`).join(",")})`;

      const { data, error } = await supabase
        .from("orders_live_ls")
        .select("orderid, ordernumber, suppliertitle, orders_decoprof_status, createdat")
        .gte("createdat", sinceIso)
        .not("suppliertitle", "is", null)
        .not("suppliertitle", "in", excludedCsv)
        .order("createdat", { ascending: false })
        .limit(10000);

      if (error) {
        console.error("[useOrdersDecoprof] Error:", error);
        throw error;
      }

      const rows = (data ?? []) as RawRow[];
      const byOrder = new Map<
        string,
        {
          orderid: number | null;
          suppliers: Set<string>;
          firstNonExcludedSupplier: string | null;
          status: OrderDecoprofStatus;
          firstSeenAt: string | null;
        }
      >();

      for (const row of rows) {
        if (!row.ordernumber) continue;
        const supplier = row.suppliertitle?.trim() || null;

        let entry = byOrder.get(row.ordernumber);
        if (!entry) {
          entry = {
            orderid: row.orderid ?? null,
            suppliers: new Set<string>(),
            firstNonExcludedSupplier: null,
            status: (row.orders_decoprof_status as OrderDecoprofStatus) || "Nieuw",
            firstSeenAt: row.createdat ?? null,
          };
          byOrder.set(row.ordernumber, entry);
        } else if (entry.orderid == null && row.orderid != null) {
          entry.orderid = row.orderid;
        }

        if (supplier) {
          entry.suppliers.add(supplier);
          if (
            !entry.firstNonExcludedSupplier &&
            !ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier)
          ) {
            entry.firstNonExcludedSupplier = supplier;
          }
        }
      }

      const result: OrdersDecoprofRow[] = [];
      for (const [ordernumber, entry] of byOrder.entries()) {
        // Een order is "Decoprof-relevant" als minstens één regel een
        // leverancier heeft die NIET in de exclude-lijst staat.
        const hasIncludedSupplier = Array.from(entry.suppliers).some(
          (s) => !ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s),
        );
        if (!hasIncludedSupplier) continue;

        const primary = entry.firstNonExcludedSupplier;
        result.push({
          ordernumber,
          orderid: entry.orderid,
          primarySupplier: primary,
          suppliers: Array.from(entry.suppliers),
          type: mapSupplierToType(primary),
          status: entry.status,
        });
      }

      return result;
    },
  });
};

/**
 * Update de Decoprof-status op alle regels van het ordernummer.
 */
export const useUpdateOrdersDecoprofStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      ordernumber: string;
      status: OrderDecoprofStatus;
    }) => {
      const { error } = await supabase
        .from("orders_live_ls")
        .update({ orders_decoprof_status: params.status })
        .eq("ordernumber", params.ordernumber);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders-decoprof"] });
    },
  });
};
